import { createApi } from '@reduxjs/toolkit/query/react';
import { createBaseQueryWithReauth } from '@/services';
import { PaginatedRequest, PaginatedResponse } from '@/types';
import { ShippingsFilter } from '@/pages/Business/pages/Shippings/models';
import { API_BUSINESS_URL } from '@/utils';

/**
 * Endpoints de envíos propios de la sección de comercios.
 *
 * Pegan contra /commerce/shippings, que expone una vista reducida y sólo
 * acepta los dos roles de comercio. El back office sigue usando
 * /business/shipping sin cambios.
 */

export interface CommerceShippingListItem {
  id: number;
  externalId: string | null;
  /** Número de seguimiento de la etiqueta; sólo es del correo si el proveedor no es SoyDelivery. */
  labelNumber: string | null;
  labelProvider: string | null;
  service: string | null;
  recipient: string;
  recipientPhoneNumber: string | null;
  deliveryAddress: string | null;
  createdAt: string | null;
  dateToDelivery: string | null;
  dateDelivered: string | null;
  status: string | null;
  statusId: string | null;
  logisticOperator: string | null;
  logisticOperatorId: string | null;
  commerce: string | null;
  commerceId: string | null;
  serviceWithOperatorId: string | null;
}

export interface CommerceShippingProduct {
  quantity: number;
  code: string | null;
  sku: string | null;
  name: string | null;
  suggestedSize: string | null;
  suggestedCategory: string | null;
  weight: number | null;
  observation: string | null;
}

export interface CommerceShippingDetail {
  id: number;
  externalId: string | null;
  labelNumber: string | null;
  labelProvider: string | null;
  statusId: string | null;
  logisticOperator: string | null;
  commerce: string | null;
  recipient: string;
  recipientPhoneNumber: string | null;
  deliveryAddress: string | null;
  deliveryLat: string | null;
  deliveryLong: string | null;
  deliveryDetails: string | null;
  createdAt: string | null;
  status: string | null;
  invoiceNumber: string | null;
  commerceReference: string | null;
  branch: string | null;
  originAddress: string | null;
  originLat: string | null;
  originLong: string | null;
  pickupDetails: string | null;
  timeSlot: { code: string | null; from: string | null; to: string | null } | null;
  size: string | null;
  service: string | null;
  dateToDelivery: string | null;
  dateDelivered: string | null;
  products: CommerceShippingProduct[];
}

export type GetCommerceShippingsArgs = {
  filters: ShippingsFilter;
  paginated: PaginatedRequest;
};

const joinIds = (ids?: string[]) =>
  ids && ids.length > 0 ? ids.join(',') : undefined;

const trimmed = (value?: string | null) => {
  const result = value?.trim();
  return result ? result : undefined;
};

const isoDate = (value?: Date | null) =>
  value ? new Date(value).toISOString() : undefined;

const commerceShippingsService = createApi({
  reducerPath: 'commerceShippingsService',
  baseQuery: createBaseQueryWithReauth(`${API_BUSINESS_URL}/commerce/shippings`),
  tagTypes: ['CommerceShippings'],
  endpoints: (builder) => ({
    getCommerceShippings: builder.query<
      PaginatedResponse<CommerceShippingListItem>,
      GetCommerceShippingsArgs
    >({
      query: ({ paginated, filters }) => {
        const base = {
          limit: paginated.pageSize,
          offset: (paginated.page - 1) * paginated.pageSize,
        };

        // Las búsquedas por identificador son excluyentes: si el usuario puso
        // un número de pedido, el resto de los filtros no aplica.
        const number = trimmed(filters.number);
        if (number) return { url: '', params: { ...base, number } };

        const externalId = trimmed(filters.commerceShippingNumber);
        if (externalId) {
          return {
            url: '',
            params: { ...base, commerceShippingNumber: externalId },
          };
        }

        const labelNumber = trimmed(filters.labelNumber);
        if (labelNumber) return { url: '', params: { ...base, labelNumber } };

        const params: Record<string, string | number> = { ...base };
        const optional: Record<string, string | number | undefined> = {
          statusId: filters.statusId,
          branchId: filters.branchId,
          commerceId: trimmed(filters.commerceId),
          recipient: trimmed(filters.recipient),
          recipientPhoneNumber: trimmed(filters.recipientPhoneNumber),
          originAddress: trimmed(filters.originAddress),
          destinationAddress: trimmed(filters.destinationAddress),
          cityIds: joinIds(filters.cityIds),
          destinationStateOrDepartmentIds: joinIds(
            filters.destinationStateOrDepartmentIds
          ),
          timeSlotIds: joinIds(filters.timeSlotIds),
          serviceWithOperatorIds: joinIds(filters.serviceWithOperatorIds),
          createdAtFrom: isoDate(filters.createdAtFrom),
          createdAtTo: isoDate(filters.createdAtTo),
          deliveryDateFrom: isoDate(filters.deliveryDateFrom),
          deliveryDateTo: isoDate(filters.deliveryDateTo),
          dateDeliveredFrom: isoDate(filters.dateDeliveredFrom),
          dateDeliveredTo: isoDate(filters.dateDeliveredTo),
        };

        Object.entries(optional).forEach(([key, value]) => {
          if (value !== undefined && value !== '') params[key] = value;
        });

        return { url: '', params };
      },
      providesTags: ['CommerceShippings'],
    }),

    getCommerceShippingByNumber: builder.query<
      CommerceShippingDetail,
      { number: string | number }
    >({
      query: ({ number }) => ({ url: `/${number}` }),
      providesTags: ['CommerceShippings'],
    }),

    cancelCommerceShipping: builder.mutation<
      { message: string },
      { number: string | number }
    >({
      query: ({ number }) => ({
        url: `/${number}/cancel`,
        method: 'POST',
      }),
      invalidatesTags: ['CommerceShippings'],
    }),
  }),
});

export default commerceShippingsService;

export const {
  useGetCommerceShippingsQuery,
  useGetCommerceShippingByNumberQuery,
  useCancelCommerceShippingMutation,
} = commerceShippingsService;
